import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { QRCodeSVG } from 'qrcode.react'
import {
  ArrowRight,
  ShieldCheck,
  Zap,
  Menu,
  X,
  ChevronRight,
  ChevronDown,
  Scale,
  Sparkles,
  QrCode,
  Printer,
  FileSpreadsheet,
  GraduationCap,
  Layers,
  Cpu,
  BookOpen,
  CheckCircle2,
  Building2,
  Scan,
  Compass,
  Clock,
  AlertTriangle,
  FileText,
  Truck,
  Radio,
  Search,
  Sun,
  Moon
} from 'lucide-react'
import { useTema } from '../../../contextos/tema/ContextoTema'
import { IsotipoLogixPort } from '../../../componentes/ui/LogoLogixPort'

// Métricas de Impacto Operativo Real
const metricasReales = [
  { valor: '100%', etiqueta: 'Trazabilidad Física', detalle: 'GS1-128 y QR 2D por pallet individual' },
  { valor: '18 Ton', etiqueta: 'Carga por Rack', detalle: 'Cálculo de tolerancia estructural en viga' },
  { valor: '3D Real', etiqueta: 'Gemelo Digital', detalle: 'Recorrido en 1ra persona con Three.js' },
  { valor: '10 Dígitos', etiqueta: 'Catálogo LIGIE', detalle: 'Fracción arancelaria y RRNA en vivo' },
]

// Módulos del Gemelo Digital WMS v1.0
const capacidadesWMS = [
  {
    icono: Layers,
    titulo: 'Gemelo Digital Tridimensional',
    descripcion: 'Navegación espacial interactiva con cámara en primera persona (FPS) y órbita 360°. Visualiza pasillos, vigas y slots como si estuvieras dentro de la nave física.',
    badge: 'Three.js / WebGL',
    estado: '100% Operativo'
  },
  {
    icono: Scale,
    titulo: 'Cálculo de Carga en Toneladas',
    descripcion: 'Ingeniería estructural estricta: cálculo de volumen en m³, peso acumulado en toneladas por nivel y semáforo dinámico de sobrecarga para racks selectivos.',
    badge: 'Ingeniería Física',
    estado: '100% Operativo'
  },
  {
    icono: QrCode,
    titulo: 'Etiquetado Térmico GS1 y QR',
    descripcion: 'Generador directo de etiquetas de corte térmico 100x75 mm con código de barras GS1-128, QR bidimensional, lote, SKU y coordenadas exactas de ubicación.',
    badge: 'Hardware Ready',
    estado: '100% Operativo'
  },
  {
    icono: BookOpen,
    titulo: 'Inteligencia Aduanera LIGIE',
    descripcion: 'Motor de clasificación arancelaria a 10 dígitos (NICO), consulta de impuestos de importación IGI/IVA e Incoterms 2020 de referencia.',
    badge: 'Comercio Exterior',
    estado: 'Consulta Activa'
  }
]

// Módulos 100% Funcionales en Producción Hoy
const modulosActivosHoy = [
  {
    icono: Layers,
    titulo: 'Gemelo Digital 3D en Tiempo Real',
    descripcion: 'Exploración interactiva en primera persona (walkthrough FPS) y órbita 360° con Three.js, raycasting para inspección de slots y configurador de naves.',
    categoria: 'WMS Core 3D',
    metrica: '100% Operativo'
  },
  {
    icono: Scale,
    titulo: 'Cálculo Estructural de Carga en Racks',
    descripcion: 'Matemática de peso en toneladas por larguero, cálculo de cubicaje en m³ y alertas dinámicas de sobrepeso para estanterías selectivas.',
    categoria: 'Ingeniería Física',
    metrica: 'Tolerancia en Toneladas'
  },
  {
    icono: QrCode,
    titulo: 'Generación Térmica GS1-128 & QR',
    descripcion: 'Etiquetado industrial estándar de 100x75 mm listo para Zebra/Brother con lote, SKU y código de 5 coordenadas [Zona-Pasillo-Rack-Nivel-Posición].',
    categoria: 'Hardware Ready',
    metrica: 'Estándar 100x75 mm'
  },
  {
    icono: Scan,
    titulo: 'Escaneo Inmediato por Cámara Móvil y Web',
    descripcion: 'Lector óptico integrado en navegador web para validación y recepción de pallets al instante sin depender de costosas terminales dedicadas.',
    categoria: 'Movilidad WMS',
    metrica: 'Detección en 0.2s'
  },
  {
    icono: FileSpreadsheet,
    titulo: 'Auditoría Kardex Inmutable con Exportación',
    descripcion: 'Registro de trazabilidad cronológica de entradas, salidas, mermas y reubicaciones con descarga directa de reportes en Excel y CSV.',
    categoria: 'Control de Inventario',
    metrica: 'Auditoría Total'
  },
  {
    icono: GraduationCap,
    titulo: 'LogixPort Campus Edition para Universidades',
    descripcion: 'Gestión académica multi-rol: creación de asignaturas, grupos de alumnos, asignación de almacenes de práctica y enrolamiento masivo por Excel.',
    categoria: 'EdTech / Facultades',
    metrica: 'Producción Activa'
  },
  {
    icono: Search,
    titulo: 'Buscador Arancelario LIGIE 2026',
    descripcion: 'Catálogo de más de 28,000 fracciones arancelarias mexicanas a 10 dígitos (NICO), descripciones, unidades de medida y tasas ad-valorem IGI/IVA.',
    categoria: 'Comercio Exterior',
    metrica: '+28,000 Fracciones'
  }
]

// Módulos en Desarrollo Activo y Roadmap
const modulosEnDesarrollo = [
  {
    icono: FileText,
    titulo: 'Motor de Dictámenes de Despacho Aduanero',
    descripcion: 'Validación automatizada de contingencias aduaneras, verificación de regulaciones no arancelarias (COFEPRIS, SE, SEMARNAT) y emisión de certificado de conformidad antes del embarque.',
    fase: 'En Modelado & Reglas Legales',
    progreso: 'Fase Prototipo',
    categoria: 'Comercio Exterior'
  },
  {
    icono: FileSpreadsheet,
    titulo: 'Simulador y Generador de Pedimentos VUCEM/SAT',
    descripcion: 'Modelado integral de pedimentos de importación/exportación con cálculo detallado de DTA, cuotas compensatorias, prevalidación fiscal y expediente aduanero digital.',
    fase: 'En Desarrollo Activo',
    progreso: 'Roadmap Q2-Q3',
    categoria: 'Fiscal / Aduanas'
  },
  {
    icono: Truck,
    titulo: 'LogixPort Enterprise (TMS, Multi-CEDIS & 3PL)',
    descripcion: 'Plataforma para grandes operadores logísticos: interconexión multi-almacén, telemetría satelital GPS de flotas en carretera, gestión de transportistas y facturación 3PL.',
    fase: 'Beta Privada en Pruebas',
    progreso: 'Próximamente',
    categoria: 'Operación Industrial'
  },
  {
    icono: Radio,
    titulo: 'Telemetría IoT de Carga en Racks & Arcos RFID',
    descripcion: 'Sensores de galgas extensiométricas en largueros metálicos para pesar pallets automáticamente sin intervención humana y arcos de lectura RFID masiva en andenes.',
    fase: 'I+D y Hardware',
    progreso: 'Roadmap Futuro',
    categoria: 'IoT / Automatización'
  }
]

// Casos de Uso Dual: Empresa (En Desarrollo) vs Universidad (Activo)
const ecosistemaDual = [
  {
    icono: GraduationCap,
    tipo: 'CAMPUS UNIVERSITARIO',
    estado: 'activo',
    badgeEstado: 'ACTIVO Y DISPONIBLE AHORA',
    titulo: 'LogixPort Campus Edition',
    subtitulo: 'Plataforma 100% activa y operativa para facultades de logística, comercio exterior y centros de formación profesional.',
    puntos: [
      'Almacén de práctica 3D con rol de operador WMS y ficha técnica de infraestructura',
      'Laboratorio de estiba con cálculo de peso en toneladas, cubicaje en m³ y etiquetado GS1',
      'Enrolamiento masivo de alumnos por Excel con jerarquía de Jefes, Docentes y Alumnos',
      'Cockpit interactivo para estudiantes con métricas de productividad viva'
    ],
    ctaTexto: 'Ingresar a Campus Edition',
    ctaLink: '/login',
    esDesarrollo: false,
    notaEstado: 'Disponible para instituciones educativas, docentes y alumnos.'
  },
  {
    icono: Building2,
    tipo: 'OPERACIÓN EMPRESARIAL',
    estado: 'desarrollo',
    badgeEstado: 'EN DESARROLLO · BETA CERRADA',
    titulo: 'LogixPort Enterprise WMS & TMS',
    subtitulo: 'Actualmente en fase activa de desarrollo e integración con operadores logísticos y agentes aduanales.',
    puntos: [
      'Control de inventario en tiempo real con auditoría Kardex inmutable (En pruebas)',
      'Asignación física de mercancía por código de ubicación (Zona-Pasillo-Rack-Nivel-Posición)',
      'Despacho aduanero asistido con cálculo de IGI, DTA e IVA según LIGIE (En desarrollo)',
      'Mantenimiento y telemetría de flota de transporte con seguimiento en ruta (Roadmap)'
    ],
    ctaTexto: 'Solicitar Acceso Anticipado (Beta)',
    ctaLink: '/register',
    esDesarrollo: true,
    notaEstado: 'Próximamente disponible para empresas de distribución y CEDIS.'
  }
]

const PaginaInicio: React.FC = () => {
  const { toggleTema, isDark } = useTema()
  const [menuAbierto, setMenuAbierto] = useState(false)
  const [tabDemo, setTabDemo] = useState<'3d' | 'qr' | 'ligie'>('3d')
  const [dropdownPlataforma, setDropdownPlataforma] = useState(false)
  const [dropdownEdiciones, setDropdownEdiciones] = useState(false)
  const [hoveredNav, setHoveredNav] = useState<string | null>(null)

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-slate-50 text-slate-900 selection:bg-cyan-500/30 dark:bg-[#030303] dark:text-zinc-100">
      
      {/* Fondo Ambiental Bento & Grid Tecnológico */}
      <div className="pointer-events-none fixed inset-0 z-0">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:28px_28px] dark:bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)]" />
        <div className="absolute top-[-10%] left-[-10%] w-[60%] h-[40%] md:w-[45%] md:h-[45%] rounded-full bg-cyan-500/20 blur-[120px] mix-blend-screen dark:bg-cyan-500/12" />
        <div className="absolute top-[30%] right-[-10%] w-[50%] h-[40%] rounded-full bg-indigo-500/20 blur-[130px] mix-blend-screen dark:bg-indigo-500/10" />
        <div className="absolute bottom-[-10%] left-[20%] w-[60%] h-[40%] rounded-full bg-blue-500/15 blur-[120px] mix-blend-screen dark:bg-blue-600/10" />
      </div>

      {/* Header Flotante Glassmorphic Premium (Logistics & Port TOS) */}
      <header className="fixed left-0 right-0 top-3 z-50 flex justify-center px-4 md:top-5">
        <motion.div
          initial={{ y: -30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="relative flex w-full max-w-5xl items-center justify-between rounded-full border border-slate-200/70 bg-white/85 px-4 py-2 shadow-[0_8px_32px_-8px_rgba(0,0,0,0.08)] backdrop-blur-2xl dark:border-white/10 dark:bg-[#08080a]/85 dark:shadow-[0_8px_32px_-8px_rgba(0,0,0,0.6)] md:px-5 md:py-2.5"
        >
          {/* Brand Logo con Isotipo Oficial Minimalista (Portal Isométrico 3D) */}
          <Link to="/" className="flex items-center gap-2.5 group shrink-0">
            <div className="relative flex h-9 w-9 items-center justify-center rounded-2xl bg-gradient-to-tr from-slate-950 via-slate-900 to-blue-950 p-1 shadow-md shadow-cyan-500/10 border border-slate-800/80 group-hover:scale-105 transition-all duration-300 dark:from-white/10 dark:via-white/5 dark:to-cyan-500/10 dark:border-white/15">
              <IsotipoLogixPort sizeCls="w-full h-full" />
            </div>
            <div className="leading-tight">
              <div className="flex items-center gap-1.5">
                <p className="text-base font-black tracking-tight text-slate-950 dark:text-white">
                  Logix<span className="text-cyan-600 dark:text-cyan-400">Port</span>
                </p>
                <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border border-cyan-500/20">
                  v1.0
                </span>
              </div>
              <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-slate-400 dark:text-zinc-500">
                PORT & LOGISTICS OS
              </p>
            </div>
          </Link>

          {/* Navegación Desktop Inteligente Desaturada (3 Hubs Claros) */}
          <nav className="hidden items-center gap-1 text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-zinc-300 md:flex">
            
            {/* Hub 1: Dropdown Plataforma */}
            <div 
              className="relative"
              onMouseEnter={() => setDropdownPlataforma(true)}
              onMouseLeave={() => setDropdownPlataforma(false)}
            >
              <button
                onClick={() => setDropdownPlataforma(v => !v)}
                onMouseEnter={() => setHoveredNav('plataforma')}
                onMouseLeave={() => setHoveredNav(null)}
                className="relative flex items-center gap-1.5 px-3.5 py-2 rounded-full transition-colors hover:text-slate-950 dark:hover:text-white"
              >
                {hoveredNav === 'plataforma' && (
                  <motion.div
                    layoutId="navHoverPill"
                    className="absolute inset-0 rounded-full bg-slate-200/60 dark:bg-white/10 -z-10"
                    transition={{ type: 'spring', stiffness: 350, damping: 25 }}
                  />
                )}
                <span>Plataforma</span>
                <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${dropdownPlataforma ? 'rotate-180 text-cyan-500' : 'text-slate-400'}`} />
              </button>

              {/* Flyout Bento Mega-Menu */}
              <AnimatePresence>
                {dropdownPlataforma && (
                  <motion.div
                    initial={{ opacity: 0, y: 12, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 8, scale: 0.96 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 28 }}
                    className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-[540px] rounded-[28px] border border-slate-200/80 bg-white/95 p-5 shadow-2xl backdrop-blur-3xl dark:border-white/10 dark:bg-[#0c0c0e]/95 z-50 overflow-hidden"
                  >
                    <div className="grid grid-cols-2 gap-4 pb-4 border-b border-slate-100 dark:border-white/5">
                      
                      {/* Columna A: WMS & FÍSICA */}
                      <div className="space-y-2.5">
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 dark:text-zinc-500 px-2">
                          WMS & Almacén Físico
                        </p>
                        <a
                          href="#digital-twin"
                          onClick={() => setDropdownPlataforma(false)}
                          className="group flex items-start gap-3 p-2.5 rounded-2xl hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
                        >
                          <div className="h-8 w-8 rounded-xl bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 flex items-center justify-center shrink-0 mt-0.5 group-hover:scale-105 transition-transform">
                            <Layers className="h-4 w-4" />
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5">
                              <p className="text-xs font-black text-slate-900 dark:text-white">Gemelo 3D</p>
                              <span className="text-[8px] font-bold px-1.5 py-0.2 rounded bg-emerald-500/10 text-emerald-500">Activo</span>
                            </div>
                            <p className="text-[11px] text-slate-500 dark:text-zinc-400 leading-tight mt-0.5">Recorrido FPS e inspección de slots en Three.js</p>
                          </div>
                        </a>

                        <a
                          href="#capacidades"
                          onClick={() => setDropdownPlataforma(false)}
                          className="group flex items-start gap-3 p-2.5 rounded-2xl hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
                        >
                          <div className="h-8 w-8 rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0 mt-0.5 group-hover:scale-105 transition-transform">
                            <Scale className="h-4 w-4" />
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5">
                              <p className="text-xs font-black text-slate-900 dark:text-white">Racks & Toneladas</p>
                              <span className="text-[8px] font-bold px-1.5 py-0.2 rounded bg-emerald-500/10 text-emerald-500">Activo</span>
                            </div>
                            <p className="text-[11px] text-slate-500 dark:text-zinc-400 leading-tight mt-0.5">Cálculo de tolerancia estructural y cubicaje</p>
                          </div>
                        </a>

                        <a
                          href="#capacidades"
                          onClick={() => setDropdownPlataforma(false)}
                          className="group flex items-start gap-3 p-2.5 rounded-2xl hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
                        >
                          <div className="h-8 w-8 rounded-xl bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0 mt-0.5 group-hover:scale-105 transition-transform">
                            <QrCode className="h-4 w-4" />
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5">
                              <p className="text-xs font-black text-slate-900 dark:text-white">Etiquetado GS1-128</p>
                              <span className="text-[8px] font-bold px-1.5 py-0.2 rounded bg-emerald-500/10 text-emerald-500">Activo</span>
                            </div>
                            <p className="text-[11px] text-slate-500 dark:text-zinc-400 leading-tight mt-0.5">Formato térmico 100x75 mm y QR 2D</p>
                          </div>
                        </a>
                      </div>

                      {/* Columna B: COMERCIO EXTERIOR */}
                      <div className="space-y-2.5">
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 dark:text-zinc-500 px-2">
                          Comercio Exterior & Despacho
                        </p>
                        <a
                          href="#aduanas"
                          onClick={() => setDropdownPlataforma(false)}
                          className="group flex items-start gap-3 p-2.5 rounded-2xl hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
                        >
                          <div className="h-8 w-8 rounded-xl bg-purple-500/10 text-purple-600 dark:text-purple-400 flex items-center justify-center shrink-0 mt-0.5 group-hover:scale-105 transition-transform">
                            <BookOpen className="h-4 w-4" />
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5">
                              <p className="text-xs font-black text-slate-900 dark:text-white">Inteligencia LIGIE</p>
                              <span className="text-[8px] font-bold px-1.5 py-0.2 rounded bg-emerald-500/10 text-emerald-500">Activo</span>
                            </div>
                            <p className="text-[11px] text-slate-500 dark:text-zinc-400 leading-tight mt-0.5">+28,000 fracciones a 10 dígitos (NICO)</p>
                          </div>
                        </a>

                        <a
                          href="#aduanas"
                          onClick={() => setDropdownPlataforma(false)}
                          className="group flex items-start gap-3 p-2.5 rounded-2xl hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
                        >
                          <div className="h-8 w-8 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0 mt-0.5 group-hover:scale-105 transition-transform">
                            <FileText className="h-4 w-4" />
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5">
                              <p className="text-xs font-black text-slate-900 dark:text-white">Dictamen Aduanero</p>
                              <span className="text-[8px] font-bold px-1.5 py-0.2 rounded bg-amber-500/10 text-amber-500">En Desarrollo</span>
                            </div>
                            <p className="text-[11px] text-slate-500 dark:text-zinc-400 leading-tight mt-0.5">Validación de contingencias y permisos</p>
                          </div>
                        </a>

                        <a
                          href="#aduanas"
                          onClick={() => setDropdownPlataforma(false)}
                          className="group flex items-start gap-3 p-2.5 rounded-2xl hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
                        >
                          <div className="h-8 w-8 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0 mt-0.5 group-hover:scale-105 transition-transform">
                            <FileSpreadsheet className="h-4 w-4" />
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5">
                              <p className="text-xs font-black text-slate-900 dark:text-white">Pedimentos VUCEM</p>
                              <span className="text-[8px] font-bold px-1.5 py-0.2 rounded bg-amber-500/10 text-amber-500">Roadmap</span>
                            </div>
                            <p className="text-[11px] text-slate-500 dark:text-zinc-400 leading-tight mt-0.5">Simulador de impuestos SAT y aranceles</p>
                          </div>
                        </a>
                      </div>

                    </div>

                    {/* Strip de Transparencia */}
                    <div className="pt-3 flex items-center justify-between">
                      <span className="text-[10px] font-bold text-slate-400 dark:text-zinc-500">
                        7 capacidades en vivo · 4 en desarrollo activo
                      </span>
                      <a
                        href="#estado-plataforma"
                        onClick={() => setDropdownPlataforma(false)}
                        className="text-[11px] font-black text-cyan-600 dark:text-cyan-400 hover:underline flex items-center gap-1"
                      >
                        <span>Ver Matriz de Capacidades</span>
                        <ChevronRight className="h-3 w-3" />
                      </a>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Hub 2: Roadmap & Estado */}
            <a
              href="#estado-plataforma"
              onMouseEnter={() => setHoveredNav('roadmap')}
              onMouseLeave={() => setHoveredNav(null)}
              className="relative flex items-center gap-1.5 px-3.5 py-2 rounded-full transition-colors hover:text-slate-950 dark:hover:text-white"
            >
              {hoveredNav === 'roadmap' && (
                <motion.div
                  layoutId="navHoverPill"
                  className="absolute inset-0 rounded-full bg-slate-200/60 dark:bg-white/10 -z-10"
                  transition={{ type: 'spring', stiffness: 350, damping: 25 }}
                />
              )}
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-cyan-500"></span>
              </span>
              <span>Roadmap 2026</span>
            </a>

            {/* Hub 3: Dropdown Ediciones */}
            <div 
              className="relative"
              onMouseEnter={() => setDropdownEdiciones(true)}
              onMouseLeave={() => setDropdownEdiciones(false)}
            >
              <button
                onClick={() => setDropdownEdiciones(v => !v)}
                onMouseEnter={() => setHoveredNav('ediciones')}
                onMouseLeave={() => setHoveredNav(null)}
                className="relative flex items-center gap-1.5 px-3.5 py-2 rounded-full transition-colors hover:text-slate-950 dark:hover:text-white"
              >
                {hoveredNav === 'ediciones' && (
                  <motion.div
                    layoutId="navHoverPill"
                    className="absolute inset-0 rounded-full bg-slate-200/60 dark:bg-white/10 -z-10"
                    transition={{ type: 'spring', stiffness: 350, damping: 25 }}
                  />
                )}
                <span>Ediciones</span>
                <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${dropdownEdiciones ? 'rotate-180 text-cyan-500' : 'text-slate-400'}`} />
              </button>

              <AnimatePresence>
                {dropdownEdiciones && (
                  <motion.div
                    initial={{ opacity: 0, y: 12, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 8, scale: 0.96 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 28 }}
                    className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-[340px] rounded-[24px] border border-slate-200/80 bg-white/95 p-3 shadow-2xl backdrop-blur-3xl dark:border-white/10 dark:bg-[#0c0c0e]/95 z-50 space-y-2"
                  >
                    <a
                      href="#ecosistema"
                      onClick={() => setDropdownEdiciones(false)}
                      className="flex items-start gap-3 p-3 rounded-2xl hover:bg-slate-100 dark:hover:bg-white/5 transition-colors group"
                    >
                      <div className="h-8 w-8 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0 mt-0.5 group-hover:scale-105 transition-transform">
                        <GraduationCap className="h-4 w-4" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-xs font-black text-slate-900 dark:text-white">Campus Edition</p>
                          <span className="text-[8px] font-bold px-1.5 py-0.2 rounded bg-emerald-500/10 text-emerald-500">Activo</span>
                        </div>
                        <p className="text-[11px] text-slate-500 dark:text-zinc-400 leading-tight mt-0.5">Para universidades, docentes y alumnos</p>
                      </div>
                    </a>

                    <a
                      href="#ecosistema"
                      onClick={() => setDropdownEdiciones(false)}
                      className="flex items-start gap-3 p-3 rounded-2xl hover:bg-slate-100 dark:hover:bg-white/5 transition-colors group"
                    >
                      <div className="h-8 w-8 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0 mt-0.5 group-hover:scale-105 transition-transform">
                        <Building2 className="h-4 w-4" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-xs font-black text-slate-900 dark:text-white">Enterprise WMS & TMS</p>
                          <span className="text-[8px] font-bold px-1.5 py-0.2 rounded bg-amber-500/10 text-amber-500">Beta</span>
                        </div>
                        <p className="text-[11px] text-slate-500 dark:text-zinc-400 leading-tight mt-0.5">Operadores logísticos, CEDIS y flotas</p>
                      </div>
                    </a>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

          </nav>

          {/* Botones de Acción (Limpio y de Alto Contraste) */}
          <div className="hidden items-center gap-2 md:flex shrink-0">
            {/* Filtro Día / Noche */}
            <button
              onClick={toggleTema}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200/80 bg-white/70 text-slate-700 hover:text-slate-950 dark:border-white/10 dark:bg-white/5 dark:text-zinc-300 dark:hover:text-white transition-all hover:scale-105 active:scale-95 shadow-sm"
              title={isDark ? "Cambiar a modo Claro (Día)" : "Cambiar a modo Oscuro (Noche)"}
              aria-label="Alternar modo día y noche"
            >
              {isDark ? (
                <Sun className="h-4 w-4 text-amber-400" />
              ) : (
                <Moon className="h-4 w-4 text-slate-700" />
              )}
            </button>

            <Link
              to="/login"
              className="px-3.5 py-2 text-xs font-bold uppercase tracking-[0.16em] text-slate-700 transition-colors hover:text-cyan-600 dark:text-zinc-300 dark:hover:text-cyan-400"
            >
              Entrar
            </Link>
            <Link
              to="/login"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-blue-600 via-indigo-600 to-cyan-600 px-5 py-2.5 text-xs font-black uppercase tracking-[0.16em] text-white shadow-md shadow-blue-500/25 transition-all hover:scale-[1.02] active:scale-[0.98] hover:shadow-cyan-500/40"
            >
              <span>Acceso Campus</span>
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>

          {/* Botón Móvil y Switch de Tema */}
          <div className="flex items-center gap-1.5 md:hidden">
            <button
              onClick={toggleTema}
              className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200/80 bg-white/70 text-slate-700 hover:text-slate-950 dark:border-white/10 dark:bg-white/5 dark:text-zinc-300 dark:hover:text-white transition-all"
              title={isDark ? "Modo Claro" : "Modo Oscuro"}
              aria-label="Alternar tema móvil"
            >
              {isDark ? <Sun className="h-4 w-4 text-amber-400" /> : <Moon className="h-4 w-4 text-slate-700" />}
            </button>
            <button
              onClick={() => setMenuAbierto((v) => !v)}
              className="rounded-xl p-2 text-slate-700 dark:text-zinc-300"
              aria-label="Abrir Menú"
            >
              {menuAbierto ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </motion.div>

        {/* Menú Móvil Desplegable Rediseñado como Command Center */}
        <AnimatePresence>
          {menuAbierto && (
            <motion.div
              initial={{ opacity: 0, y: -20, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 350, damping: 26 }}
              className="absolute left-4 right-4 top-16 max-h-[85vh] overflow-y-auto rounded-[32px] border border-slate-200/80 bg-white/95 p-6 shadow-2xl backdrop-blur-2xl dark:border-white/10 dark:bg-[#0c0c0e]/95 md:hidden"
            >
              <div className="flex flex-col gap-4">
                
                {/* Sección Almacén */}
                <div>
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 dark:text-zinc-500 mb-2">
                    WMS & Almacén 3D
                  </p>
                  <div className="grid grid-cols-1 gap-1.5">
                    <a href="#digital-twin" onClick={() => setMenuAbierto(false)} className="flex items-center justify-between rounded-xl px-3.5 py-2.5 text-sm font-bold text-slate-900 hover:bg-slate-100 dark:text-white dark:hover:bg-white/5">
                      <span className="flex items-center gap-2"><Layers className="h-4 w-4 text-cyan-500" /> Gemelo Digital 3D</span>
                      <span className="text-[9px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 font-bold">Activo</span>
                    </a>
                    <a href="#capacidades" onClick={() => setMenuAbierto(false)} className="flex items-center justify-between rounded-xl px-3.5 py-2.5 text-sm font-bold text-slate-900 hover:bg-slate-100 dark:text-white dark:hover:bg-white/5">
                      <span className="flex items-center gap-2"><Scale className="h-4 w-4 text-blue-500" /> Racks & Toneladas</span>
                      <span className="text-[9px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 font-bold">Activo</span>
                    </a>
                    <a href="#capacidades" onClick={() => setMenuAbierto(false)} className="flex items-center justify-between rounded-xl px-3.5 py-2.5 text-sm font-bold text-slate-900 hover:bg-slate-100 dark:text-white dark:hover:bg-white/5">
                      <span className="flex items-center gap-2"><QrCode className="h-4 w-4 text-indigo-500" /> Etiquetado Térmico GS1</span>
                      <span className="text-[9px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 font-bold">Activo</span>
                    </a>
                  </div>
                </div>

                {/* Sección Comercio Exterior */}
                <div>
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 dark:text-zinc-500 mb-2">
                    Comercio Exterior & Aduanas
                  </p>
                  <div className="grid grid-cols-1 gap-1.5">
                    <a href="#aduanas" onClick={() => setMenuAbierto(false)} className="flex items-center justify-between rounded-xl px-3.5 py-2.5 text-sm font-bold text-slate-900 hover:bg-slate-100 dark:text-white dark:hover:bg-white/5">
                      <span className="flex items-center gap-2"><BookOpen className="h-4 w-4 text-purple-500" /> Catálogo LIGIE 2026</span>
                      <span className="text-[9px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 font-bold">Activo</span>
                    </a>
                    <a href="#aduanas" onClick={() => setMenuAbierto(false)} className="flex items-center justify-between rounded-xl px-3.5 py-2.5 text-sm font-bold text-slate-500 hover:bg-slate-100 dark:text-zinc-400 dark:hover:bg-white/5">
                      <span className="flex items-center gap-2"><FileText className="h-4 w-4 text-amber-500" /> Dictámenes Aduaneros</span>
                      <span className="text-[9px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-500 font-bold">En Desarrollo</span>
                    </a>
                  </div>
                </div>

                {/* Sección Roadmap & Ediciones */}
                <div className="pt-2 border-t border-slate-200/60 dark:border-white/5">
                  <a href="#estado-plataforma" onClick={() => setMenuAbierto(false)} className="flex items-center justify-between rounded-xl px-3.5 py-2.5 text-sm font-bold text-slate-900 hover:bg-slate-100 dark:text-white dark:hover:bg-white/5">
                    <span className="flex items-center gap-2"><Clock className="h-4 w-4 text-cyan-500" /> Matriz de Roadmap</span>
                    <span className="text-[9px] px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 font-bold">Transparencia</span>
                  </a>
                  <a href="#ecosistema" onClick={() => setMenuAbierto(false)} className="flex items-center justify-between rounded-xl px-3.5 py-2.5 text-sm font-bold text-slate-900 hover:bg-slate-100 dark:text-white dark:hover:bg-white/5">
                    <span className="flex items-center gap-2"><GraduationCap className="h-4 w-4 text-emerald-500" /> Campus Edition</span>
                    <span className="text-[9px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 font-bold">Activo</span>
                  </a>
                  <a href="#ecosistema" onClick={() => setMenuAbierto(false)} className="flex items-center justify-between rounded-xl px-3.5 py-2.5 text-sm font-bold text-slate-500 hover:bg-slate-100 dark:text-zinc-400 dark:hover:bg-white/5">
                    <span className="flex items-center gap-2"><Building2 className="h-4 w-4 text-amber-500" /> LogixPort Empresarial</span>
                    <span className="text-[9px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-500 font-bold">Beta</span>
                  </a>
                </div>

                {/* Switch de Tema Móvil */}
                <div className="pt-2 border-t border-slate-200/60 dark:border-white/5 flex items-center justify-between px-2">
                  <span className="text-xs font-bold text-slate-500 dark:text-zinc-400">Modo de Interfaz</span>
                  <button
                    onClick={toggleTema}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200/80 bg-slate-100 text-slate-800 dark:border-white/10 dark:bg-white/5 dark:text-zinc-200 text-xs font-bold"
                  >
                    {isDark ? <Sun className="h-3.5 w-3.5 text-amber-400" /> : <Moon className="h-3.5 w-3.5 text-slate-700" />}
                    <span>{isDark ? 'Tema Claro' : 'Tema Oscuro'}</span>
                  </button>
                </div>

                <div className="pt-2">
                  <Link to="/login" className="w-full rounded-2xl bg-gradient-to-r from-blue-600 via-indigo-600 to-cyan-600 px-4 py-3.5 text-center text-sm font-black uppercase tracking-wider text-white shadow-lg shadow-blue-500/25 flex items-center justify-center gap-2">
                    <span>Entrar a Campus Edition</span>
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* ========================================================================= */}
      {/* SECCIÓN HERO PRINCIPAL: BENTO COCKPIT WMS v1.0 */}
      {/* ========================================================================= */}
      <section className="relative z-10 px-4 pb-16 pt-28 md:px-8 md:pb-24 md:pt-40">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-12 lg:grid-cols-[minmax(0,1.05fr)_540px] lg:items-center">
            
            {/* Columna Izquierda: Mensaje y Propuesta de Valor */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 rounded-full border border-cyan-500/25 bg-cyan-500/10 px-4 py-1.5 text-[11px] font-black uppercase tracking-[0.2em] text-cyan-600 dark:text-cyan-400"
              >
                <Zap className="h-3.5 w-3.5 animate-pulse" />
                CAMPUS EDITION ACTIVO · GEMELOS 3D · WMS INDUSTRIAL
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.08 }}
                className="mt-6 text-4xl font-black tracking-tight text-slate-950 dark:text-white sm:text-6xl lg:text-7xl leading-[1.06]"
              >
                La física del almacén,
                <span className="block bg-gradient-to-r from-blue-600 via-cyan-500 to-indigo-500 bg-clip-text text-transparent dark:from-cyan-400 dark:via-blue-400 dark:to-indigo-300">
                  digitalizada en 3D
                </span>
                y sincronizada en tiempo real.
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.16 }}
                className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 dark:text-zinc-400 md:text-lg font-normal"
              >
                LogixPort unifica el gemelo tridimensional interactivo, cálculo estructural de racks en toneladas, 
                etiquetas térmicas GS1-128, clasificación arancelaria LIGIE y el laboratorio de prácticas para universidades y centros logísticos.
              </motion.p>

              {/* Botones CTA Principales */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.24 }}
                className="mt-8 flex flex-col gap-3.5 sm:flex-row"
              >
                <Link
                  to="/login"
                  className="group inline-flex h-14 items-center justify-center gap-3 rounded-2xl bg-gradient-to-r from-blue-600 via-indigo-600 to-cyan-600 px-8 text-xs font-black uppercase tracking-[0.18em] text-white shadow-xl shadow-blue-500/25 transition-all hover:scale-[1.02] active:scale-[0.99] hover:shadow-cyan-500/40"
                >
                  <GraduationCap className="h-4 w-4" />
                  <span>Entrar a Campus Edition</span>
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
                <a
                  href="#ecosistema"
                  className="inline-flex h-14 items-center justify-center gap-2 rounded-2xl border border-slate-200/80 bg-white/80 px-8 text-xs font-bold uppercase tracking-[0.16em] text-slate-700 shadow-sm backdrop-blur-xl transition-all hover:bg-white dark:border-white/10 dark:bg-white/5 dark:text-zinc-200 dark:hover:bg-white/10"
                >
                  <Building2 className="h-4 w-4 text-amber-500" />
                  <span>Empresarial (En Desarrollo)</span>
                </a>
              </motion.div>

              {/* Bento Strip de Métricas Vivas */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.32 }}
                className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-4"
              >
                {metricasReales.map((m) => (
                  <div
                    key={m.etiqueta}
                    className="rounded-2xl border border-slate-200/80 bg-white/70 p-4 shadow-sm backdrop-blur-xl dark:border-white/[0.08] dark:bg-[#0c0c0c]/80"
                  >
                    <p className="text-xl sm:text-2xl font-black tracking-tight text-slate-900 dark:text-white font-mono">{m.valor}</p>
                    <p className="mt-1 text-xs font-bold text-slate-700 dark:text-zinc-200 leading-tight">{m.etiqueta}</p>
                    <p className="mt-1 text-[11px] leading-tight text-slate-400 dark:text-zinc-500">{m.detalle}</p>
                  </div>
                ))}
              </motion.div>
            </div>

            {/* Columna Derecha: Consola Bento Interactiva Viva */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="relative"
            >
              {/* Resplandor Ambiental */}
              <div className="absolute inset-0 rounded-[36px] bg-gradient-to-tr from-cyan-500/20 via-blue-500/10 to-transparent blur-3xl" />

              <div className="relative overflow-hidden rounded-[32px] border border-slate-200/80 bg-white/90 p-4 shadow-2xl backdrop-blur-2xl dark:border-white/10 dark:bg-[#0c0c0c]/90">
                
                {/* Header de la Consola Virtual */}
                <div className="flex items-center justify-between border-b border-slate-200/60 dark:border-white/10 pb-3 mb-4">
                  <div className="flex items-center gap-2">
                    <span className="h-3 w-3 rounded-full bg-rose-500/80 inline-block" />
                    <span className="h-3 w-3 rounded-full bg-amber-500/80 inline-block" />
                    <span className="h-3 w-3 rounded-full bg-emerald-500/80 inline-block" />
                    <span className="ml-2 text-xs font-bold font-mono text-slate-500 dark:text-zinc-400">TELEMETRÍA LOGIXPORT · ONLINE</span>
                  </div>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping" />
                    SISTEMA ACTIVO
                  </span>
                </div>

                {/* Selector de Pestañas Interactivas */}
                <div className="flex items-center gap-1.5 p-1 rounded-2xl bg-slate-100/90 dark:bg-white/5 border border-slate-200/60 dark:border-white/5 mb-4">
                  <button
                    onClick={() => setTabDemo('3d')}
                    className={`flex-1 py-2 px-3 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 ${
                      tabDemo === '3d'
                        ? 'bg-white dark:bg-white/10 text-blue-600 dark:text-white shadow-sm'
                        : 'text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white'
                    }`}
                  >
                    <Layers className="h-3.5 w-3.5" />
                    <span>Gemelo 3D</span>
                  </button>
                  <button
                    onClick={() => setTabDemo('qr')}
                    className={`flex-1 py-2 px-3 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 ${
                      tabDemo === 'qr'
                        ? 'bg-white dark:bg-white/10 text-blue-600 dark:text-white shadow-sm'
                        : 'text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white'
                    }`}
                  >
                    <QrCode className="h-3.5 w-3.5" />
                    <span>Etiqueta GS1</span>
                  </button>
                  <button
                    onClick={() => setTabDemo('ligie')}
                    className={`flex-1 py-2 px-3 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 ${
                      tabDemo === 'ligie'
                        ? 'bg-white dark:bg-white/10 text-blue-600 dark:text-white shadow-sm'
                        : 'text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white'
                    }`}
                  >
                    <BookOpen className="h-3.5 w-3.5" />
                    <span>Aduana LIGIE</span>
                  </button>
                </div>

                {/* Vistas Dinámicas según Pestaña Seleccionada */}
                <div className="min-h-[320px] rounded-2xl border border-slate-200/60 dark:border-white/5 bg-slate-50/50 dark:bg-black/40 p-4 flex flex-col justify-between">
                  {tabDemo === '3d' && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-wider">Rack Seleccionado</p>
                          <h4 className="text-lg font-black text-slate-900 dark:text-white font-mono">ALM-01 · SEC · P01 · R01</h4>
                        </div>
                        <span className="px-2.5 py-1 rounded-lg text-[10px] font-black uppercase bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20">
                          ZONA SECA
                        </span>
                      </div>

                      {/* Mockup de Rack Isométrico */}
                      <div className="p-3 rounded-xl bg-white dark:bg-[#111113] border border-slate-200/80 dark:border-white/10 space-y-2.5">
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-bold text-slate-700 dark:text-zinc-300">N3 · Nivel Superior (Pallets ligeros)</span>
                          <span className="font-mono text-emerald-500 font-bold">2/8 Slots</span>
                        </div>
                        <div className="grid grid-cols-8 gap-1.5 h-6">
                          {[1, 2].map((s) => (
                            <div key={`n3-${s}`} className="rounded bg-cyan-500/80 dark:bg-cyan-500/60 flex items-center justify-center text-[9px] font-black text-white" title="Ocupado">
                              P
                            </div>
                          ))}
                          {[3, 4, 5, 6, 7, 8].map((s) => (
                            <div key={`n3-e-${s}`} className="rounded border border-dashed border-slate-300 dark:border-white/20 bg-slate-100/50 dark:bg-white/[0.02]" title="Libre" />
                          ))}
                        </div>

                        <div className="flex items-center justify-between text-xs pt-1">
                          <span className="font-bold text-slate-700 dark:text-zinc-300">N2 · Nivel Medio</span>
                          <span className="font-mono text-cyan-500 font-bold">5/8 Slots</span>
                        </div>
                        <div className="grid grid-cols-8 gap-1.5 h-6">
                          {[1, 2, 3, 4, 5].map((s) => (
                            <div key={`n2-${s}`} className="rounded bg-blue-500/80 dark:bg-blue-500/60 flex items-center justify-center text-[9px] font-black text-white" title="Ocupado">
                              P
                            </div>
                          ))}
                          {[6, 7, 8].map((s) => (
                            <div key={`n2-e-${s}`} className="rounded border border-dashed border-slate-300 dark:border-white/20 bg-slate-100/50 dark:bg-white/[0.02]" title="Libre" />
                          ))}
                        </div>

                        <div className="flex items-center justify-between text-xs pt-1">
                          <span className="font-bold text-slate-700 dark:text-zinc-300">N1 · Nivel Suelo (Carga pesada)</span>
                          <span className="font-mono text-amber-500 font-bold">8/8 Slots (Lleno)</span>
                        </div>
                        <div className="grid grid-cols-8 gap-1.5 h-6">
                          {[1, 2, 3, 4, 5, 6, 7, 8].map((s) => (
                            <div key={`n1-${s}`} className="rounded bg-amber-500/80 dark:bg-amber-500/60 flex items-center justify-center text-[9px] font-black text-white" title="Ocupado">
                              P
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Telemetría Estructural en Toneladas */}
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="p-2 rounded-xl bg-white/70 dark:bg-white/[0.03] border border-slate-200/60 dark:border-white/5">
                          <p className="text-[10px] text-slate-400 font-bold uppercase">Carga Total</p>
                          <p className="text-sm font-black text-slate-900 dark:text-white font-mono">11.4 Ton</p>
                        </div>
                        <div className="p-2 rounded-xl bg-white/70 dark:bg-white/[0.03] border border-slate-200/60 dark:border-white/5">
                          <p className="text-[10px] text-slate-400 font-bold uppercase">Límite Viga</p>
                          <p className="text-sm font-black text-slate-900 dark:text-white font-mono">18.0 Ton</p>
                        </div>
                        <div className="p-2 rounded-xl bg-white/70 dark:bg-white/[0.03] border border-slate-200/60 dark:border-white/5">
                          <p className="text-[10px] text-slate-400 font-bold uppercase">Ocupación</p>
                          <p className="text-sm font-black text-emerald-500 font-mono">62.5%</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {tabDemo === 'qr' && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-wider">Etiqueta Térmica GS1-128</p>
                          <h4 className="text-base font-black text-slate-900 dark:text-white">Estándar 100 x 75 mm</h4>
                        </div>
                        <span className="px-2 py-0.5 rounded text-[10px] font-black uppercase bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">
                          LISTA PARA IMPRESIÓN
                        </span>
                      </div>

                      {/* Mockup de Etiqueta Térmica Real */}
                      <div className="p-3.5 rounded-xl bg-white text-black border-2 border-slate-300 shadow-md space-y-2">
                        <div className="flex items-start justify-between border-b-2 border-black pb-1.5">
                          <div>
                            <span className="text-[8px] font-black tracking-widest uppercase block text-zinc-500">ID PALLET LOGIXPORT</span>
                            <span className="text-sm font-black font-mono">PAL-10023-MX</span>
                          </div>
                          <div className="text-right">
                            <span className="text-[8px] font-bold block text-zinc-500">UBICACIÓN</span>
                            <span className="text-xs font-black font-mono bg-black text-white px-1.5 py-0.5 rounded">
                              SEC-P01-R01-N02-S03
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center justify-between gap-3 pt-1">
                          <div className="p-1 border border-black rounded bg-white">
                            <QRCodeSVG value="LOGIXPORT:PAL-10023-MX:SEC-P01-R01-N02-S03" size={64} />
                          </div>
                          <div className="flex-1 space-y-0.5">
                            <p className="text-[9px] font-bold text-zinc-600">PRODUCTO: <span className="text-black font-black">Válvulas Industriales 2"</span></p>
                            <p className="text-[9px] font-bold text-zinc-600">LOTE: <span className="text-black font-mono">LOT-2026-B89</span></p>
                            <p className="text-[9px] font-bold text-zinc-600">PESO: <span className="text-black font-mono">480 kg · 0.85 m³</span></p>
                            <div className="h-5 w-full bg-zinc-200 mt-1 flex items-center justify-center font-mono text-[8px] tracking-widest">
                              ||||| | |||| ||| |||||| | ||
                            </div>
                          </div>
                        </div>

                        <div className="border-t border-dashed border-zinc-400 pt-1 flex items-center justify-between text-[7px] text-zinc-500">
                          <span>[CORTE TÉRMICO ESTÁNDAR]</span>
                          <span>AUDITORÍA KARDEX VERIFICADA</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between text-xs text-slate-500 dark:text-zinc-400 pt-1">
                        <span className="flex items-center gap-1.5 font-bold">
                          <Printer className="h-3.5 w-3.5 text-blue-500" />
                          Compatible con Zebra / Brother
                        </span>
                        <span className="font-mono text-cyan-500 font-bold">Escaneo con Cámara 0.2s</span>
                      </div>
                    </div>
                  )}

                  {tabDemo === 'ligie' && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-wider">Consultor Arancelario</p>
                          <h4 className="text-base font-black text-slate-900 dark:text-white">Fracción LIGIE en Vivo</h4>
                        </div>
                        <span className="px-2 py-0.5 rounded text-[10px] font-black uppercase bg-purple-500/10 text-purple-400 border border-purple-500/20">
                          VIGENTE 2026
                        </span>
                      </div>

                      {/* Mockup de Fracción Arancelaria */}
                      <div className="p-3.5 rounded-xl bg-white dark:bg-[#111113] border border-slate-200/80 dark:border-white/10 space-y-2.5">
                        <div className="flex items-center justify-between">
                          <span className="text-base font-mono font-black text-cyan-600 dark:text-cyan-400">
                            8471.30.01.00
                          </span>
                          <span className="text-xs font-bold font-mono px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">
                            IGI: EXENTO · IVA: 16%
                          </span>
                        </div>
                        <p className="text-xs font-medium text-slate-600 dark:text-zinc-300 leading-snug">
                          Máquinas automáticas para tratamiento o procesamiento de datos, portátiles, de peso inferior o igual a 10 kg.
                        </p>
                        
                        <div className="grid grid-cols-2 gap-2 pt-1 border-t border-slate-100 dark:border-white/5">
                          <div className="p-2 rounded bg-slate-50 dark:bg-white/[0.02]">
                            <span className="text-[9px] font-bold text-slate-400 block uppercase">Regulación RRNA</span>
                            <span className="text-xs font-bold text-slate-700 dark:text-zinc-200">NOM-019-SCFI (Seguridad)</span>
                          </div>
                          <div className="p-2 rounded bg-slate-50 dark:bg-white/[0.02]">
                            <span className="text-[9px] font-bold text-slate-400 block uppercase">Incoterm Sugerido</span>
                            <span className="text-xs font-bold text-slate-700 dark:text-zinc-200">FOB Manzanillo / DDP</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between text-xs text-slate-500 dark:text-zinc-400 pt-1">
                        <span className="flex items-center gap-1.5 font-bold">
                          <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                            Dictamen en Desarrollo
                          </span>
                        </span>
                        <span className="font-mono text-cyan-500 font-bold">+28,000 Fracciones</span>
                      </div>
                    </div>
                  )}

                  {/* Footer de la tarjeta con link a acción */}
                  <div className="pt-3 border-t border-slate-200/60 dark:border-white/5 flex items-center justify-between">
                    <span className="text-[11px] font-bold text-slate-400 dark:text-zinc-500">
                      Buscador activo · Dictámenes en desarrollo
                    </span>
                    <a
                      href="#aduanas"
                      className="text-xs font-black text-cyan-600 dark:text-cyan-400 hover:underline flex items-center gap-1"
                    >
                      <span>Ver Roadmap Aduanero</span>
                      <ChevronRight className="h-3.5 w-3.5" />
                    </a>
                  </div>
                </div>

              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* SECCIÓN 1: EL GEMELO DIGITAL 3D (DIGITAL TWIN THREE.JS) */}
      {/* ========================================================================= */}
      <section id="digital-twin" className="relative z-10 px-4 py-16 md:px-8 md:py-24 border-t border-slate-200/60 dark:border-white/[0.05]">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 text-blue-600 dark:text-cyan-400 text-xs font-black uppercase tracking-wider mb-4 border border-blue-500/20">
              <Compass className="h-3.5 w-3.5" />
              INSPECCIÓN ESPACIAL TRIDIMENSIONAL
            </div>
            <h2 className="text-3xl font-black tracking-tight text-slate-950 dark:text-white md:text-5xl">
              Camina por tu almacén físico desde cualquier navegador.
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600 dark:text-zinc-400 md:text-lg">
              Dejamos atrás las tablas 2D planas. LogixPort renderiza una réplica exacta de tu nave con 
              estanterías metálicas, pasillos de maniobra, pallets estibados y raycasting para inspeccionar cada slot con un clic.
            </p>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-3">
            <div className="rounded-[28px] border border-slate-200/80 bg-white/70 p-6 md:p-8 shadow-sm backdrop-blur-xl dark:border-white/[0.08] dark:bg-[#0c0c0c]/80 group hover:border-cyan-500/40 transition-all">
              <div className="h-12 w-12 rounded-2xl bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 flex items-center justify-center mb-6">
                <Compass className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">Cámara en 1ra Persona (FPS)</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-zinc-400">
                Control estilo walkthrough con teclado (WASD) y ratón para recorrer pasillos de paletización, simulando la vista del operador o montacarguista en campo.
              </p>
            </div>

            <div className="rounded-[28px] border border-slate-200/80 bg-white/70 p-6 md:p-8 shadow-sm backdrop-blur-xl dark:border-white/[0.08] dark:bg-[#0c0c0c]/80 group hover:border-blue-500/40 transition-all">
              <div className="h-12 w-12 rounded-2xl bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center mb-6">
                <Scan className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">Inspección de Slots por Clic</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-zinc-400">
                Selecciona cualquier hueco en el espacio 3D para abrir su ficha técnica inmediata: SKU, peso en kg, volumen en m³, lote de caducidad y trazabilidad de entrada.
              </p>
            </div>

            <div className="rounded-[28px] border border-slate-200/80 bg-white/70 p-6 md:p-8 shadow-sm backdrop-blur-xl dark:border-white/[0.08] dark:bg-[#0c0c0c]/80 group hover:border-indigo-500/40 transition-all">
              <div className="h-12 w-12 rounded-2xl bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center mb-6">
                <Cpu className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">Editor Arquitectónico de Nave</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-zinc-400">
                Configura zonas operativas (seco, refrigerado, picking, cuarentena), define ancho de vigas en metros, altura en niveles y número de estanterías en tiempo real.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* SECCIÓN 2: BENTO GRID DE CAPACIDADES WMS E INGENIERÍA */}
      {/* ========================================================================= */}
      <section id="capacidades" className="relative z-10 px-4 py-16 md:px-8 md:py-24 border-t border-slate-200/60 dark:border-white/[0.05]">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.2em] text-cyan-600 dark:text-cyan-400">Ingeniería WMS & Almacén Físico</p>
              <h2 className="mt-2 text-3xl font-black tracking-tight text-slate-950 dark:text-white md:text-5xl">
                Ingeniería de almacenamiento sin improvisación.
              </h2>
            </div>
            <p className="text-sm md:text-base text-slate-500 dark:text-zinc-400 max-w-md">
              Cada componente fue diseñado bajo estándares de almacenaje industrial real: resistencia de materiales, rotación FIFO/FEFO y trazabilidad física GS1.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {capacidadesWMS.map((cap) => (
              <div
                key={cap.titulo}
                className="rounded-[28px] border border-slate-200/80 bg-white/70 p-6 md:p-7 shadow-sm backdrop-blur-xl dark:border-white/[0.08] dark:bg-[#0c0c0c]/80 flex flex-col justify-between hover:border-cyan-500/40 transition-all group"
              >
                <div>
                  <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
                    <div className="h-12 w-12 rounded-2xl bg-slate-900 text-white dark:bg-white/10 dark:text-cyan-300 flex items-center justify-center shadow-md">
                      <cap.icono className="h-6 w-6" />
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap justify-end">
                      <span className="text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded-md bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-zinc-400 border border-slate-200 dark:border-white/10">
                        {cap.badge}
                      </span>
                      <span className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full ${
                        cap.estado === '100% Operativo'
                          ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20'
                          : 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border border-cyan-500/20'
                      }`}>
                        {cap.estado}
                      </span>
                    </div>
                  </div>
                  <h3 className="text-lg font-black text-slate-900 dark:text-white tracking-tight">{cap.titulo}</h3>
                  <p className="mt-2.5 text-xs sm:text-sm leading-relaxed text-slate-600 dark:text-zinc-400">{cap.descripcion}</p>
                </div>

                <div className="mt-6 pt-4 border-t border-slate-100 dark:border-white/5 flex items-center text-xs font-bold text-cyan-600 dark:text-cyan-400 group-hover:translate-x-1 transition-transform">
                  <span>Conocer especificación</span>
                  <ChevronRight className="h-3.5 w-3.5 ml-1" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* SECCIÓN 3: INTELIGENCIA ADUANERA Y CLASIFICACIÓN LIGIE */}
      {/* ========================================================================= */}
      <section id="aduanas" className="relative z-10 px-4 py-16 md:px-8 md:py-24 border-t border-slate-200/60 dark:border-white/[0.05]">
        <div className="mx-auto max-w-7xl">
          <div className="rounded-[36px] border border-slate-200/80 bg-gradient-to-br from-white/90 via-white/70 to-slate-100/80 p-8 shadow-xl backdrop-blur-2xl dark:border-white/10 dark:from-[#0c0c0c]/90 dark:via-[#0c0c0c]/70 dark:to-[#070707]/90 md:p-12 overflow-hidden relative">
            <div className="pointer-events-none absolute -right-20 -top-20 w-96 h-96 rounded-full bg-cyan-500/10 blur-3xl" />
            <div className="pointer-events-none absolute right-40 -bottom-20 w-80 h-80 rounded-full bg-indigo-500/10 blur-3xl" />

            <div className="relative z-10 grid gap-10 lg:grid-cols-[minmax(0,1fr)_460px] items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/10 text-purple-600 dark:text-purple-400 text-xs font-black uppercase tracking-wider mb-4 border border-purple-500/20">
                  <BookOpen className="h-3.5 w-3.5" />
                  MOTOR LIGIE ACTIVO · DICTÁMENES EN DESARROLLO
                </div>
                <h2 className="text-3xl font-black tracking-tight text-slate-950 dark:text-white md:text-5xl">
                  Clasificación arancelaria en vivo y dictámenes aduaneros en construcción.
                </h2>
                <p className="mt-4 text-base leading-relaxed text-slate-600 dark:text-zinc-400 md:text-lg">
                  Conectamos la física del almacén con la legislación de comercio exterior en México. Hoy dispones de consulta activa en el catálogo LIGIE 2026 a 10 dígitos (NICO); paralelamente, estamos desarrollando el motor de dictámenes legales y simulación de pedimentos aduanales.
                </p>

                <div className="mt-8 grid gap-4 sm:grid-cols-2">
                  <div className="flex flex-col justify-between p-4 rounded-2xl bg-white/60 dark:bg-white/[0.03] border border-slate-200/60 dark:border-white/5">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
                      <div>
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h4 className="text-sm font-black text-slate-900 dark:text-white">Clasificación a 10 Dígitos</h4>
                          <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                            Activo Hoy
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 dark:text-zinc-400">Capítulo, partida, subpartida, fracción y NICO conforme a la LIGIE mexicana 2026.</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col justify-between p-4 rounded-2xl bg-white/60 dark:bg-white/[0.03] border border-slate-200/60 dark:border-white/5">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
                      <div>
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h4 className="text-sm font-black text-slate-900 dark:text-white">Matriz Incoterms 2020</h4>
                          <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                            Activo Hoy
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 dark:text-zinc-400">Asignación de responsabilidades de flete, seguro y riesgo (FOB, CIF, DDP, EXW) en recepción.</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col justify-between p-4 rounded-2xl bg-amber-500/[0.04] dark:bg-amber-500/[0.02] border border-amber-500/25">
                    <div className="flex items-start gap-3">
                      <Clock className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                      <div>
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h4 className="text-sm font-black text-slate-900 dark:text-white">Dictamen de Despacho</h4>
                          <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                            En Desarrollo
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 dark:text-zinc-400">Evaluación automática de contingencias aduaneras, permisos COFEPRIS/SE y emisión de conformidad.</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col justify-between p-4 rounded-2xl bg-amber-500/[0.04] dark:bg-amber-500/[0.02] border border-amber-500/25">
                    <div className="flex items-start gap-3">
                      <Clock className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                      <div>
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h4 className="text-sm font-black text-slate-900 dark:text-white">Simulador de Pedimentos</h4>
                          <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                            Próximamente
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 dark:text-zinc-400">Cálculo integral de contribuciones al comercio exterior (IGI, DTA, prevalidación e IVA SAT).</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tarjeta Visual de Dictamen Aduanero (Prototipo en Desarrollo) */}
              <div className="rounded-[28px] border border-amber-500/30 bg-white dark:bg-[#111114] p-6 shadow-xl space-y-4 relative overflow-hidden">
                <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-white/10">
                  <div className="flex items-center gap-2">
                    <FileSpreadsheet className="h-5 w-5 text-amber-500" />
                    <span className="text-xs font-black uppercase text-slate-800 dark:text-white font-mono">PROTOTIPO DICTAMEN ADUANERO</span>
                  </div>
                  <span className="px-2.5 py-0.5 rounded text-[10px] font-black uppercase bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/25 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                    EN DESARROLLO
                  </span>
                </div>

                {/* Banner de Aviso de Desarrollo */}
                <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-xs text-amber-800 dark:text-amber-300 flex items-start gap-2.5">
                  <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400 mt-0.5" />
                  <p className="text-[11px] leading-relaxed">
                    <strong>Módulo en desarrollo:</strong> Se está diseñando para auditar automáticamente discrepancias de despacho, permisos no arancelarios y armado de expediente aduanero previo a pedimento.
                  </p>
                </div>

                <div className="space-y-2 text-xs bg-slate-50/70 dark:bg-white/[0.02] p-3 rounded-xl border border-slate-200/60 dark:border-white/5">
                  <div className="flex justify-between py-1 border-b border-slate-200/60 dark:border-white/5">
                    <span className="text-slate-400 font-medium">Mercancía:</span>
                    <span className="font-bold text-slate-900 dark:text-white">Autopartes de Transmisión</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-slate-200/60 dark:border-white/5">
                    <span className="text-slate-400 font-medium">Fracción LIGIE:</span>
                    <span className="font-mono font-bold text-cyan-600 dark:text-cyan-400">8708.29.99.00</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-slate-200/60 dark:border-white/5">
                    <span className="text-slate-400 font-medium">Arancel IGI:</span>
                    <span className="font-mono font-bold text-slate-900 dark:text-white">5.0% Ad-valorem</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-slate-200/60 dark:border-white/5">
                    <span className="text-slate-400 font-medium">Incoterm:</span>
                    <span className="font-bold text-slate-900 dark:text-white">FOB Puerto de Veracruz</span>
                  </div>
                  <div className="flex justify-between py-1">
                    <span className="text-slate-400 font-medium">Permiso RRNA:</span>
                    <span className="font-bold text-amber-500">SE Certificado Requerido</span>
                  </div>
                </div>

                <div className="space-y-2 pt-1">
                  <Link
                    to="/login"
                    className="w-full py-3 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 text-white text-xs font-black uppercase tracking-wider flex items-center justify-center gap-2 transition-all hover:scale-[1.01] shadow-md shadow-blue-500/20"
                  >
                    <Search className="h-3.5 w-3.5" />
                    <span>Explorar Catálogo LIGIE (Activo)</span>
                  </Link>
                  <a
                    href="#estado-plataforma"
                    className="w-full py-2 rounded-xl text-[11px] font-bold text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white text-center block transition-colors"
                  >
                    Ver matriz completa de roadmap &darr;
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* SECCIÓN NUEVA: TRANSPARENCIA TECNOLÓGICA: LO FUNCIONAL HOY VS ROADMAP */}
      {/* ========================================================================= */}
      <section id="estado-plataforma" className="relative z-10 px-4 py-16 md:px-8 md:py-24 border-t border-slate-200/60 dark:border-white/[0.05]">
        <div className="mx-auto max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 text-xs font-black uppercase tracking-wider mb-4 border border-cyan-500/20">
              <ShieldCheck className="h-3.5 w-3.5" />
              TRANSPARENCIA TECNOLÓGICA · MATRIZ DE CAPACIDADES
            </div>
            <h2 className="text-3xl font-black tracking-tight text-slate-950 dark:text-white md:text-5xl">
              Lo que está 100% funcional hoy vs lo que se agregará.
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600 dark:text-zinc-400">
              Mantenemos un compromiso absoluto de transparencia hacia universidades, estudiantes y empresas. Conoce con claridad qué puedes utilizar en producción hoy y qué estamos construyendo.
            </p>
          </div>

          <div className="grid gap-8 lg:grid-cols-2">
            
            {/* PANEL 1: FUNCIONAL Y DISPONIBLE HOY */}
            <div className="rounded-[32px] border-2 border-emerald-500/40 bg-white/90 p-6 md:p-8 shadow-xl backdrop-blur-2xl dark:border-emerald-500/30 dark:bg-[#09100d]/90 flex flex-col justify-between relative overflow-hidden">
              <div className="pointer-events-none absolute -right-20 -top-20 w-64 h-64 rounded-full bg-emerald-500/10 blur-3xl" />
              
              <div>
                <div className="flex items-center justify-between pb-6 border-b border-slate-200/60 dark:border-white/10 mb-6 flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <div className="h-11 w-11 rounded-2xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center border border-emerald-500/20">
                      <CheckCircle2 className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="text-xl font-black text-slate-900 dark:text-white">100% Funcional y Operativo</h3>
                      <p className="text-xs font-bold text-emerald-600 dark:text-emerald-400">Disponible hoy en LogixPort Campus Edition</p>
                    </div>
                  </div>
                  <span className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                    7 Módulos Activos
                  </span>
                </div>

                <div className="space-y-4">
                  {modulosActivosHoy.map((mod) => (
                    <div
                      key={mod.titulo}
                      className="p-4 rounded-2xl border border-slate-200/60 bg-white/70 dark:border-white/5 dark:bg-white/[0.02] flex items-start gap-3.5 hover:border-emerald-500/30 transition-colors"
                    >
                      <div className="h-9 w-9 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0 mt-0.5">
                        <mod.icono className="h-4 w-4" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                          <h4 className="text-sm font-black text-slate-900 dark:text-white">{mod.titulo}</h4>
                          <span className="text-[9px] font-mono font-bold px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                            {mod.metrica}
                          </span>
                        </div>
                        <p className="text-xs text-slate-600 dark:text-zinc-400 leading-relaxed">{mod.descripcion}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-slate-200/60 dark:border-white/10 flex items-center justify-between flex-wrap gap-4">
                <span className="text-xs font-bold text-slate-500 dark:text-zinc-400">
                  Listo para clases prácticas y simulación
                </span>
                <Link
                  to="/login"
                  className="px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-black uppercase tracking-wider transition-all shadow-md shadow-emerald-500/20 flex items-center gap-2"
                >
                  <span>Ingresar a Campus</span>
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>

            {/* PANEL 2: EN DESARROLLO ACTIVO Y ROADMAP */}
            <div className="rounded-[32px] border-2 border-amber-500/40 bg-white/90 p-6 md:p-8 shadow-xl backdrop-blur-2xl dark:border-amber-500/30 dark:bg-[#120f0a]/90 flex flex-col justify-between relative overflow-hidden">
              <div className="pointer-events-none absolute -right-20 -top-20 w-64 h-64 rounded-full bg-amber-500/10 blur-3xl" />
              
              <div>
                <div className="flex items-center justify-between pb-6 border-b border-slate-200/60 dark:border-white/10 mb-6 flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <div className="h-11 w-11 rounded-2xl bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center border border-amber-500/20">
                      <Clock className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="text-xl font-black text-slate-900 dark:text-white">En Desarrollo Activo & Roadmap</h3>
                      <p className="text-xs font-bold text-amber-600 dark:text-amber-400">En fase de ingeniería y modelado legal</p>
                    </div>
                  </div>
                  <span className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                    4 Módulos en Camino
                  </span>
                </div>

                <div className="space-y-4">
                  {modulosEnDesarrollo.map((mod) => (
                    <div
                      key={mod.titulo}
                      className="p-4 rounded-2xl border border-amber-500/20 bg-amber-500/[0.02] dark:border-amber-500/15 dark:bg-white/[0.01] flex items-start gap-3.5 hover:border-amber-500/40 transition-colors"
                    >
                      <div className="h-9 w-9 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0 mt-0.5">
                        <mod.icono className="h-4 w-4" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                          <h4 className="text-sm font-black text-slate-900 dark:text-white">{mod.titulo}</h4>
                          <span className="text-[9px] font-mono font-black uppercase tracking-wider px-2 py-0.5 rounded bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/25">
                            {mod.fase}
                          </span>
                        </div>
                        <p className="text-xs text-slate-600 dark:text-zinc-400 leading-relaxed mb-2">{mod.descripcion}</p>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-bold text-slate-400 dark:text-zinc-500">Estimación:</span>
                          <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400">{mod.progreso}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-slate-200/60 dark:border-white/10 flex items-center justify-between flex-wrap gap-4">
                <span className="text-xs font-bold text-slate-500 dark:text-zinc-400">
                  ¿Quieres sugerir una integración o función?
                </span>
                <Link
                  to="/register"
                  className="px-6 py-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-white dark:bg-white/10 dark:hover:bg-white/20 text-xs font-black uppercase tracking-wider transition-all border border-slate-300 dark:border-white/10 flex items-center gap-2"
                >
                  <span>Anotarse al Beta Privado</span>
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* SECCIÓN 4: ECOSISTEMA DUAL: EMPRESA VS CAMPUS EDITION */}
      {/* ========================================================================= */}
      <section id="ecosistema" className="relative z-10 px-4 py-16 md:px-8 md:py-24 border-t border-slate-200/60 dark:border-white/[0.05]">
        <div className="mx-auto max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-cyan-600 dark:text-cyan-400">Ecosistema Integrado</p>
            <h2 className="mt-2 text-3xl font-black tracking-tight text-slate-950 dark:text-white md:text-5xl">
              Diseñado tanto para la industria como para la academia.
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600 dark:text-zinc-400">
              Un solo motor tecnológico con dos interfaces especializadas: potencia operativa para empresas y laboratorios de simulación para universidades.
            </p>
          </div>

          <div className="grid gap-8 lg:grid-cols-2">
            {ecosistemaDual.map((item) => (
              <div
                key={item.tipo}
                className={`rounded-[32px] p-8 shadow-sm backdrop-blur-xl flex flex-col justify-between transition-all group relative overflow-hidden ${
                  item.esDesarrollo
                    ? 'border border-amber-500/30 bg-white/70 dark:bg-[#0c0c0c]/80 dark:border-amber-500/20 hover:border-amber-500/50'
                    : 'border-2 border-cyan-500/50 bg-white/95 dark:bg-[#0b0d14]/95 dark:border-cyan-500/40 shadow-xl shadow-cyan-500/10 ring-1 ring-cyan-500/20'
                }`}
              >
                {/* Glow ambiental para la versión activa */}
                {!item.esDesarrollo && (
                  <div className="pointer-events-none absolute -right-20 -top-20 w-60 h-60 rounded-full bg-cyan-500/10 blur-2xl" />
                )}

                <div>
                  {/* Badge de Estado Operativo */}
                  <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${
                      item.esDesarrollo
                        ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/25'
                        : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/25 shadow-sm'
                    }`}>
                      <span className={`w-2 h-2 rounded-full ${item.esDesarrollo ? 'bg-amber-500' : 'bg-emerald-500 animate-pulse'}`} />
                      {item.badgeEstado}
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-zinc-500">
                      {item.tipo}
                    </span>
                  </div>

                  <div className="flex items-center gap-3 mb-4">
                    <div className={`h-12 w-12 rounded-2xl flex items-center justify-center shadow-md ${
                      item.esDesarrollo
                        ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20'
                        : 'bg-gradient-to-tr from-cyan-600 to-blue-600 text-white shadow-cyan-500/25'
                    }`}>
                      <item.icono className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">{item.titulo}</h3>
                      <p className="text-[11px] font-bold text-slate-400 dark:text-zinc-500">{item.notaEstado}</p>
                    </div>
                  </div>

                  <p className="text-sm font-medium text-slate-600 dark:text-zinc-300 mb-6 leading-relaxed">
                    {item.subtitulo}
                  </p>

                  <div className="space-y-3 mb-8">
                    {item.puntos.map((punto, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <CheckCircle2 className={`h-4 w-4 shrink-0 mt-0.5 ${item.esDesarrollo ? 'text-amber-500' : 'text-cyan-500'}`} />
                        <span className="text-xs sm:text-sm font-medium text-slate-700 dark:text-zinc-300 leading-normal">{punto}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <Link
                  to={item.ctaLink}
                  className={`w-full py-4 rounded-2xl text-xs font-black uppercase tracking-[0.16em] flex items-center justify-center gap-2 transition-all hover:scale-[1.01] ${
                    item.esDesarrollo
                      ? 'bg-slate-900 hover:bg-slate-800 text-white dark:bg-white/10 dark:hover:bg-white/15 dark:text-zinc-200 border border-slate-300 dark:border-white/10 shadow-sm'
                      : 'bg-gradient-to-r from-blue-600 via-indigo-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white shadow-xl shadow-blue-500/30'
                  }`}
                >
                  <span>{item.ctaTexto}</span>
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* SECCIÓN FINAL CTA */}
      {/* ========================================================================= */}
      <section className="relative z-10 px-4 py-16 md:px-8 md:py-24">
        <div className="mx-auto max-w-5xl rounded-[36px] bg-gradient-to-r from-blue-600 via-indigo-600 to-cyan-600 p-8 md:p-14 text-center text-white shadow-2xl relative overflow-hidden">
          <div className="pointer-events-none absolute -right-10 -bottom-10 w-72 h-72 rounded-full bg-white/10 blur-2xl" />
          
          <div className="relative z-10 max-w-2xl mx-auto space-y-6">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 text-white text-xs font-black uppercase tracking-wider backdrop-blur-md">
              <Sparkles className="h-3.5 w-3.5" />
              DIGITALIZACIÓN LOGÍSTICA INMEDIATA
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight leading-tight">
              Lleva tu operación y formación logística al estándar digital v1.0.
            </h2>
            <p className="text-sm md:text-base text-white/80 font-normal leading-relaxed">
              Descubre cómo LogixPort unifica el gemelo tridimensional, la ingeniería estructural de racks y el cumplimiento aduanero en un solo ecosistema.
            </p>

            <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/register"
                className="w-full sm:w-auto h-13 px-8 rounded-2xl bg-white text-slate-950 font-black text-xs uppercase tracking-[0.18em] shadow-lg transition-all hover:scale-105 active:scale-95 flex items-center justify-center gap-2"
              >
                <span>Crear Cuenta Gratuita</span>
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/login"
                className="w-full sm:w-auto h-13 px-8 rounded-2xl bg-black/20 hover:bg-black/30 border border-white/20 text-white font-bold text-xs uppercase tracking-[0.18em] transition-all flex items-center justify-center"
              >
                <span>Iniciar Sesión</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* FOOTER */}
      {/* ========================================================================= */}
      <footer className="relative z-10 border-t border-slate-200/60 bg-white/40 px-4 py-10 backdrop-blur-xl dark:border-white/10 dark:bg-black/40 md:px-8">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-6 md:flex-row">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-gradient-to-tr from-slate-950 via-slate-900 to-blue-950 p-1 shadow-md dark:from-white/10 dark:via-white/5 dark:to-cyan-500/10 dark:border-white/15">
              <IsotipoLogixPort sizeCls="w-full h-full" />
            </div>
            <div>
              <p className="text-sm font-black tracking-tight text-slate-950 dark:text-white">Logix<span className="text-cyan-600 dark:text-cyan-400">Port</span> <span className="text-xs font-black uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border border-cyan-500/20 ml-1">v1.0</span></p>
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400 dark:text-zinc-500">
                Digital Twin & Trade Operations
              </p>
            </div>
          </div>

          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-zinc-500 text-center md:text-right">
            © {new Date().getFullYear()} LogixPort Systems Inc.
            <span className="hidden md:inline"> · </span>
            Powered by <a href="https://vespex.pro" target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:text-cyan-500 dark:text-cyan-400 transition-colors">VESPEX PRO</a>
          </p>
        </div>
      </footer>

    </div>
  )
}

export default PaginaInicio
